import Async from "components/DesignSystem/AsyncComponents/Async";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import React from "react";
import { useGetAllPayrollsQuery } from "store/apis/payroll";
import { useDebounce } from "utils/debounce";
import ConnectGusto from "./ConnectGusto";
import ImportModal from "./ImportModal";
import PayrollTable from "./PayrollTable";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { SmallWarningInfo } from "components/icons/SmallWarningInfo";
import { HeaderActions } from "components/AppLayout/HeaderActions";

type PayrollContentProps = {
  entityId: string;
  groupId: string;
};
export default function PayrollContent({
  entityId,
  groupId,
}: PayrollContentProps) {
  const { update: updateQuery } = useUpdateQuery();
  const query = useQuery();
  const searchText = query.get("search");
  const pageNum = query.get("page_num") || 1;
  const page_num = Number(pageNum);
  const search_term = useDebounce(searchText);

  const { data, isLoading, isSuccess, isError, isFetching } =
    useGetAllPayrollsQuery(
      {
        entityId,
        page_num,
        search_term,
      },
      {
        skip: !entityId,
      }
    );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateQuery({ query: "search", value: e.target.value || null });
  };

  return (
    <div className="t-flex t-flex-col t-gap-4 t-relative t-pb-20">
      <HeaderActions>
        <div className="t-flex t-gap-2">
          <ConnectGusto entityId={entityId} />
          <LockedUi
            feature={WRITE_PERMISSION_KEYS.EXPENSE}
            blockedUi={<LockedButton text="Import" size="xsmall" />}
          >
            <ImportModal entityId={entityId} />
          </LockedUi>
        </div>
      </HeaderActions>

      <DashboardContainer.Header sticky>
        <div className="t-flex t-gap-4 t-justify-between t-items-center">
          <Search
            isLoading={isFetching}
            onChange={handleSearchChange}
            defaultValue={searchText || ""}
            className="t-w-[30%] focus:t-w-3/6 t-transition-all"
          />
          <PaginationHandler
            page_num={page_num}
            per_page={data?.per_page || 1}
            total_count={data?.total_count || 0}
            total_pages={data?.total_pages || 1}
            onPageNumChange={(page) => {
              updateQuery({
                query: "page_num",
                value: page,
              });
            }}
            isLoading={isFetching}
          />
        </div>
      </DashboardContainer.Header>

      <Async.Root
        isEmpty={data?.payrolls.length === 0}
        isLoading={isLoading}
        isError={isError}
        isSuccess={isSuccess}
      >
        <Async.Empty>
          <div className="t-text-center t-text-text-60 t-text-body-sm">
            <PayrollTable payrolls={[]} entityId={entityId} groupId={groupId} />
            <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-2 t-h-[calc(100vh-300px)]">
              <SmallWarningInfo className="t-text-neutral-10 t-h-[46px] t-w-[46px]" />
              <div className="t-text-subtext t-text-text-60">
                No payrolls reports added yet
              </div>
              <div className="t-text-body-sm t-text-text-60">
                Get started by importing reports
              </div>
            </div>
          </div>
        </Async.Empty>
        <Async.Success>
          <PayrollTable
            payrolls={data?.payrolls || []}
            entityId={entityId}
            groupId={groupId}
          />
        </Async.Success>
      </Async.Root>
    </div>
  );
}
